/**
 * Poster modal — one-click server poster for a published end-product.
 *
 * Distinct from the client SocialComposer (which overlays text on a raw image):
 * this calls the server poster endpoint, which uses the product's REAL white base
 * + designed image + title + price and bakes the text server-side (satori+resvg,
 * any-language safe). Opened from a product/design context that carries an
 * end_product_id. why -> bao 2026-06-26 (server endpoint outputs the full poster).
 */

import { generatePoster } from '../../api/poster.js'
import { store } from '../../core/store.js'
import { showToast } from '../../utils/toast.js'

const TEMPLATES = [
  { id: 'hero', name: 'Hero' },
  { id: 'showcase', name: 'Blank → design' },
]
const PLATFORMS = [
  { id: 'portrait', name: 'Portrait 3:4' },
  { id: 'story', name: 'Story 9:16' },
  { id: 'square', name: 'Square 1:1' },
]

let _root = null
let _st = null

/**
 * @param {{ endProductId: string, title?: string, shareUrl?: string }} opts
 * shareUrl = canonical PDP link already carrying ?share=<token> (H-176 token);
 * when present the modal offers a copy-link action next to the PNG download.
 */
export function openPosterModal({ endProductId, title, shareUrl }) {
  if (!endProductId) { showToast('No product to poster', 'warning'); return }
  if (_root) close()
  _st = { endProductId, title: title || '', template: 'hero', platform: 'portrait', eyebrow: '', cta: 'Shop now', url: null, shareUrl: shareUrl || null, loading: false }
  injectStyles()
  render()
}

function render() {
  _root = document.createElement('div')
  _root.className = 'pm-overlay'
  _root.innerHTML = `
    <div class="pm-modal" role="dialog" aria-label="Make a poster">
      <div class="pm-head">
        <h3>Make a poster</h3>
        <button class="pm-x" id="pmClose" aria-label="Close"><i class="ph ph-x"></i></button>
      </div>
      <div class="pm-body">
        <div class="pm-preview" id="pmPreview">
          <div class="pm-ph"><i class="ph ph-image"></i><span>Pick a style and generate</span></div>
        </div>
        <div class="pm-controls">
          <label class="pm-grp"><span>Template</span>
            <div class="pm-seg" id="pmTemplate">${TEMPLATES.map(t => `<button data-id="${t.id}" class="${t.id === _st.template ? 'on' : ''}">${t.name}</button>`).join('')}</div>
          </label>
          <label class="pm-grp"><span>Format</span>
            <div class="pm-seg" id="pmPlatform">${PLATFORMS.map(p => `<button data-id="${p.id}" class="${p.id === _st.platform ? 'on' : ''}">${p.name}</button>`).join('')}</div>
          </label>
          <label class="pm-grp"><span>Eyebrow / tag</span><input type="text" id="pmEyebrow" maxlength="40" value="" placeholder="e.g. NEW DROP" /></label>
          <label class="pm-grp"><span>Call to action</span><input type="text" id="pmCta" maxlength="40" value="${escAttr(_st.cta)}" placeholder="e.g. Shop now" /></label>
          <button class="pm-gen" id="pmGen"><i class="ph ph-sparkle"></i> Generate poster</button>
          <a class="pm-dl" id="pmDl" style="display:none" download="mamamiya-poster.png"><i class="ph ph-download-simple"></i> Download</a>
          <button class="pm-copy" id="pmCopy" style="display:none"><i class="ph ph-link"></i> Copy PDP link</button>
        </div>
      </div>
    </div>`
  document.body.appendChild(_root)
  _root.querySelector('#pmClose').addEventListener('click', close)
  _root.addEventListener('click', e => { if (e.target === _root) close() })
  document.addEventListener('keydown', onKey)
  seg('#pmTemplate', id => { _st.template = id })
  seg('#pmPlatform', id => { _st.platform = id })
  _root.querySelector('#pmEyebrow').addEventListener('input', e => { _st.eyebrow = e.target.value })
  _root.querySelector('#pmCta').addEventListener('input', e => { _st.cta = e.target.value })
  _root.querySelector('#pmGen').addEventListener('click', generate)
  _root.querySelector('#pmCopy').addEventListener('click', copyShareLink)
  _root.querySelector('#pmDl').addEventListener('click', downloadPoster)
}

// The poster lives on the CDN (cross-origin), where the anchor `download`
// attribute is ignored — a plain click would navigate to the inline PNG and
// lose the mamamiya-poster.png name. Fetch the bytes (CDN sends CORS *),
// then download via a same-origin blob URL so the filename always sticks.
async function downloadPoster(e) {
  e.preventDefault()
  if (!_st?.url) return
  const dl = _root.querySelector('#pmDl')
  const label = dl.innerHTML
  dl.innerHTML = '<i class="ph ph-circle-notch pm-spin"></i> Downloading…'
  try {
    const res = await fetch(_st.url, { mode: 'cors' })
    if (!res.ok) throw new Error(`poster fetch ${res.status}`)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = 'mamamiya-poster.png'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
  } catch (err) {
    showToast(`Download failed: ${err.message || 'try again'}`, 'error')
  } finally {
    dl.innerHTML = label
  }
}

async function copyShareLink() {
  if (!_st?.shareUrl) return
  try {
    await navigator.clipboard.writeText(_st.shareUrl)
    showToast('PDP link copied', 'success', 1500)
  } catch {
    showToast('Copy failed', 'error')
  }
}

function seg(sel, cb) {
  const el = _root.querySelector(sel)
  el.addEventListener('click', e => {
    const b = e.target.closest('button'); if (!b) return
    el.querySelectorAll('button').forEach(x => x.classList.remove('on'))
    b.classList.add('on'); cb(b.dataset.id)
  })
}

async function generate() {
  if (_st.loading) return
  if (!store.getState('auth.token')) { showToast('Please sign in to make a poster', 'warning'); return }
  const btn = _root.querySelector('#pmGen')
  _st.loading = true; btn.disabled = true
  const label = btn.innerHTML
  btn.innerHTML = '<i class="ph ph-circle-notch pm-spin"></i> Rendering…'
  try {
    const res = await generatePoster({
      endProductId: _st.endProductId,
      template: _st.template,
      platform: _st.platform,
      eyebrow: _st.eyebrow || undefined,
      cta: _st.cta || undefined,
    })
    const url = res?.asset?.public_url
    if (!url) throw new Error('no poster url')
    _st.url = url
    const prev = _root.querySelector('#pmPreview')
    prev.innerHTML = `<img src="${escAttr(url)}" alt="poster" title="Click to view full size" />`
    // Click the poster to view it full-size (full image, not the fitted preview).
    prev.querySelector('img').addEventListener('click', () => window.open(url, '_blank', 'noopener'))
    const dl = _root.querySelector('#pmDl')
    dl.href = url; dl.style.display = ''
    const copy = _root.querySelector('#pmCopy')
    if (copy && _st.shareUrl) copy.style.display = ''
    showToast('Poster ready', 'success', 1500)
  } catch (err) {
    showToast(`Poster failed: ${err.message || 'try again'}`, 'error')
  } finally {
    _st.loading = false; btn.disabled = false; btn.innerHTML = label
  }
}

function onKey(e) { if (e.key === 'Escape') close() }
function close() { document.removeEventListener('keydown', onKey); _root?.remove(); _root = null; _st = null }
function escAttr(s) { return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])) }

function injectStyles() {
  if (document.getElementById('pmStyles')) return
  const s = document.createElement('style')
  s.id = 'pmStyles'
  s.textContent = `
.pm-overlay{position:fixed;inset:0;z-index:1000;background:var(--bg-overlay);display:flex;align-items:center;justify-content:center;padding:24px}
.pm-modal{background:var(--surface-s1);color:var(--text-primary);border:1px solid var(--border-default);border-radius:16px;width:min(880px,96vw);max-height:92vh;display:flex;flex-direction:column;overflow:hidden}
.pm-head{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border-bottom:1px solid var(--border-subtle)}
.pm-head h3{margin:0;font-size:16px;font-family:var(--font-display)}
.pm-x{background:none;border:none;color:inherit;font-size:20px;cursor:pointer;opacity:.7}.pm-x:hover{opacity:1}
.pm-body{display:grid;grid-template-columns:1fr 280px;gap:18px;padding:18px;overflow:auto}
/* Fixed-height frame: reserved BEFORE the poster loads so the layout never jumps
   when the image arrives or the aspect changes (3:4 / 9:16 / 1:1). why -> bao: stable layout. */
.pm-preview{display:flex;align-items:center;justify-content:center;background:var(--surface-bg);border-radius:12px;padding:16px;height:64vh;min-height:320px}
.pm-preview img{max-width:100%;max-height:100%;object-fit:contain;border-radius:6px;box-shadow:var(--elev-3);cursor:zoom-in}
.pm-ph{display:flex;flex-direction:column;align-items:center;gap:8px;color:var(--text-secondary);font-size:13px}.pm-ph i{font-size:30px}
.pm-controls{display:flex;flex-direction:column;gap:14px}
.pm-grp{display:flex;flex-direction:column;gap:6px;font-size:12px;color:var(--text-secondary)}
.pm-grp input{background:var(--surface-s2);border:1px solid var(--border-default);color:var(--text-primary);border-radius:8px;padding:8px 10px;font-size:13px;font-family:inherit}
.pm-seg{display:flex;gap:6px;flex-wrap:wrap}
.pm-seg button{flex:1;min-width:72px;background:var(--surface-s2);border:1px solid var(--border-default);color:var(--text-secondary);border-radius:8px;padding:7px 8px;font-size:12px;cursor:pointer}
.pm-seg button.on{background:var(--brand-primary);border-color:var(--brand-primary);color:var(--brand-primary-fg)}
.pm-gen{background:var(--brand-primary);color:var(--brand-primary-fg);border:none;border-radius:10px;padding:11px;font-size:14px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px}
.pm-gen:disabled{opacity:.6;cursor:default}
.pm-dl{background:var(--surface-s2);border:1px solid var(--border-default);color:var(--text-primary);border-radius:10px;padding:10px;font-size:13px;text-decoration:none;display:flex;align-items:center;justify-content:center;gap:6px}
.pm-copy{background:var(--surface-s2);border:1px solid var(--border-default);color:var(--text-primary);border-radius:10px;padding:10px;font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;font-family:inherit}
.pm-spin{display:inline-block;animation:pm-rot 1s linear infinite}@keyframes pm-rot{to{transform:rotate(360deg)}}
@media(max-width:720px){.pm-body{grid-template-columns:1fr}}
`
  document.head.appendChild(s)
}
